// src/context/socket.js
// Better-than-FAANG+ signaling layer for the global A/V system.

import React, { createContext, useContext, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { showToast, primeToastAudio } from '../services/toastService';
import mitt from 'mitt';
import { AuthContext } from './AuthContext';
import { SOCKET_URL } from '../socketConfig';
/* ============================================================
   SOCKET INIT
============================================================ */

const allowGuest =
  String(process.env.REACT_APP_SOCKET_ALLOW_GUEST || 'true') === 'true';
const ATTR_STORAGE_KEY = 'jg_guest_attrib';
const presenceMetricsDebug =
  String(process.env.REACT_APP_PRESENCE_DEBUG || '').toLowerCase() === 'true';

const getStoredAttribution = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(ATTR_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const allowedKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'region',
      'referrer',
    ];
    const out = {};
    allowedKeys.forEach((k) => {
      if (parsed[k]) out[k] = parsed[k];
    });
    return out;
  } catch (e) {
    return {};
  }
};

export const audio = new Audio(
  'https://res.cloudinary.com/joegalaxy/video/upload/v1658385502/Whatsapp_Message_-_QuickSounds.com_u2jgir.mp3',
);
export const snakeAudio = new Audio(
  'https://res.cloudinary.com/joegalaxy/video/upload/q_auto/v1670911462/snake_sflx2f.mp3',
);
export const dreamerAudio = new Audio(
  'https://res.cloudinary.com/joegalaxy/video/upload/v1671776001/Images/dreamer-88881_dfyepq.mp3',
);

export const socket = io(SOCKET_URL, {
  transports: ['websocket', 'polling'], // prefer websocket first, fallback to polling
  autoConnect: allowGuest,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 500,
  reconnectionDelayMax: 5000,
});

socket.on('connect', () => {
  console.log('[socket] connected', { id: socket.id, url: SOCKET_URL });
});

/* ============================================================
   CUSTOM RTT LATENCY (WORKS IN PURE WEBSOCKET MODE)
============================================================ */
let __jgLatencyMs = null;

socket.on('latency:pong', (sentAt) => {
  __jgLatencyMs = Date.now() - sentAt;
  // console.log('[RTT]', __jgLatencyMs, 'ms');
});

setInterval(() => {
  socket.emit('latency:ping', Date.now());
}, 5000);

export function getLatencyMs() {
  return __jgLatencyMs;
}

/* ============================================================
   DEBUG
============================================================ */
socket.onAny((event, data) => {
  if (event.startsWith('call:') || event.startsWith('webrtc'))
    console.log('[socket:onAny inbound]', event, data);
});

/* ============================================================
   OUTBOUND EMIT ENRICHMENT
============================================================ */
{
  const __emit = socket.emit.bind(socket);
  socket.emit = (event, payload = {}, ...rest) => {
    if (
      typeof event === 'string' &&
      (event.startsWith('call:') || event.startsWith('webrtc'))
    ) {
      const self = window.__jg?.self || {};
      const enriched = {
        ...payload,
        from: payload.from || self.userId,
        fromUserId: payload.fromUserId || self.userId,
        fromPlayerId: payload.fromPlayerId || self.playerId,
        fromName: payload.fromName || self.name || self.playerId,
        fromProfilePhoto:
          payload.fromProfilePhoto ||
          self.profilePhoto ||
          payload.profilePhoto ||
          null,
      };

      console.log('[socket emit →]', event, enriched);
      return __emit(event, enriched, ...rest);
    }
    return __emit(event, payload, ...rest);
  };
}

/* ============================================================
   mitt() event bus
============================================================ */
export const callEvents = mitt();

/* ============================================================
   INBOUND NORMALIZERS
============================================================ */
const pickUserId = (p) =>
  p?.fromUserId || p?.from || p?.userId || p?.callerId || p?.senderId;

const pickHandle = (p) =>
  p?.fromPlayerId || p?.playerId || p?.handle || p?.fromHandle || p?.username;

const pickSdp = (p) =>
  p?.sdp || p?.offer || p?.answer || (typeof p === 'string' ? p : null);

const pickCandidate = (p) => p?.candidate || p?.iceCandidate || p?.ice;

/* ============================================================
   OFFER PAYLOAD NORMALIZER — NEW A/V CONTRACT
============================================================ */
const normalizeOfferPayload = (raw = {}) => {
  const fromUserId = pickUserId(raw);
  const fromPlayerId = pickHandle(raw);
  const remoteOffer = pickSdp(raw);
  const explicitIsVideo =
    Object.prototype.hasOwnProperty.call(raw, 'isVideo') ||
    Object.prototype.hasOwnProperty.call(raw, 'withVideo')
      ? raw.isVideo ?? raw.withVideo
      : undefined;

  return {
    raw,
    fromUserId,
    fromPlayerId,
    convoId: raw.convoId || raw.callId || raw.callID,
    sdp: remoteOffer,
    explicitIsVideo,
  };
};

/* ============================================================
   ANSWER & ICE NORMALIZERS
============================================================ */
const normalizeAnswerPayload = (p) => ({
  sdp: pickSdp(p),
  raw: p,
});

const normalizeIcePayload = (p) => ({
  candidate: pickCandidate(p),
  raw: p,
});

/* ============================================================
   SERVER → CLIENT
============================================================ */

// --- Basic call lifecycle
socket.on('call:incoming', (p) => callEvents.emit('incoming', p));
socket.on('call:cancelled', (p) => callEvents.emit('cancelled', p));
socket.on('call:timeout', (p) => callEvents.emit('timeout', p));
socket.on('call:ended', (p) => callEvents.emit('ended', p));

// --- Offer Path (🔥 main refactor)
socket.on('webrtc:offer', (p) =>
  callEvents.emit('offer', normalizeOfferPayload(p)),
);

// --- Answer Path
socket.on('webrtc:answer', (p) =>
  callEvents.emit('answer', normalizeAnswerPayload(p)),
);

// --- ICE
socket.on('webrtc:ice', (p) => callEvents.emit('ice', normalizeIcePayload(p)));

/* ============================================================
   call:offer — FULL NORMALIZATION
============================================================ */
socket.on('call:offer', (payload) => {
  console.log('[socket] call:offer inbound:', payload);

  const norm = normalizeOfferPayload(payload);
  let offer = norm.sdp;

  // SDP -> object shape
  if (offer && typeof offer === 'string') offer = { type: 'offer', sdp: offer };

  // Fallback SDP sniff if explicit flag missing
  let isVideo;
  if (typeof norm.explicitIsVideo === 'boolean') {
    isVideo = norm.explicitIsVideo;
  } else {
    const sdpText = offer?.sdp || '';
    isVideo = sdpText.includes('m=video');
  }

  const incoming = {
    ...payload,
    fromUserId: norm.fromUserId,
    fromPlayerId: norm.fromPlayerId,
    fromName: payload.fromName || norm.fromPlayerId || 'Incoming call',
    fromProfilePhoto:
      payload.fromProfilePhoto ||
      payload.profilePhoto ||
      payload.avatar ||
      null,
    offer,
    convoId: norm.convoId,
    isVideo,
  };

  // 1️⃣ Pass to WebRTC path
  callEvents.emit('offer', incoming);

  // 2️⃣ Pass to UI path
  callEvents.emit('incoming', incoming);

  console.log('[socket] → mapped final incoming offer:', incoming);
});

/* ============================================================
   Legacy compatibility
============================================================ */
socket.on('call:answer', (p) =>
  callEvents.emit('answer', normalizeAnswerPayload(p)),
);

socket.on('call:candidate', (p) =>
  callEvents.emit('ice', normalizeIcePayload(p)),
);

socket.on('call:hangup', (p) => callEvents.emit('ended', p));
socket.on('call:declined', (p) => callEvents.emit('cancelled', p));
socket.on('call:decline', (p) => callEvents.emit('cancelled', p));
socket.on('call:end', (p) => callEvents.emit('ended', p));

/* ============================================================
   SOCKET PROVIDER
============================================================ */
export const SocketContext = createContext(socket);
export const useSocket = () => useContext(SocketContext);

export function SocketProvider({ token, playerId, children }) {
  const { player } = useContext(AuthContext);
  const presenceCallbacksRef = useRef({
    snapshot: new Set(),
    diff: new Set(),
  });
  // Store callbacks for PresenceContext to subscribe
  const socketPresenceCallbacks = {
    snapshot: new Set(),
    diff: new Set(),
  };

  // Sync identity into window.__jg
  useEffect(() => {
    if (!player?.data?.player) return;
    const p = player.data.player;

    window.__jg = window.__jg || {};
    window.__jg.self = {
      userId: p._id || null,
      playerId: p.playerId || null,
      name: p.name || p.playerId || null,
      profilePhoto: p.profilePhoto || null,
    };
  }, [player]);

  // Reconnect on token
  useEffect(() => {
    const attribution = getStoredAttribution();
    socket.auth = token ? { ...attribution, token } : { ...attribution };
    if (socket.connected) {
      socket.disconnect();
      socket.connect();
    } else if (token || allowGuest) {
      socket.connect();
    }
  }, [token]);

  // API exposed to PresenceContext
  const socketPresence = {
    onSnapshot: (fn) => socketPresenceCallbacks.snapshot.add(fn),
    offSnapshot: (fn) => socketPresenceCallbacks.snapshot.delete(fn),

    onDiff: (fn) => socketPresenceCallbacks.diff.add(fn),
    offDiff: (fn) => socketPresenceCallbacks.diff.delete(fn),
  };

  // On connect
  useEffect(() => {
    if (!socket) return;
    let cleanupPresenceMetrics = null;

    // Utility normalizer for ALL presence payloads
    const normalizePresence = (p = {}) => {
      // Debug: raw presence payload from backend
      console.debug('[presence normalize] raw payload:', p);

      const id = p.playerId || p.mongoId || p._id || p.handle || null;

      if (!id) {
        console.warn('[presence normalize] missing identity → payload:', p);
        return null;
      }

      // NEW: Use presenceStatus if provided by the backend, otherwise fallback
      const status =
        p.status ??
        p.presenceStatus ??
        (typeof p.online !== 'undefined'
          ? p.online
            ? 'online'
            : 'offline'
          : 'online');

      // Debug: show how status is resolved
      console.debug('[presence normalize] resolved status:', {
        rawStatus: p.status,
        presenceStatus: p.presenceStatus,
        legacyOnlineFlag: p.online,
        finalStatus: status,
      });

      const presenceObj = {
        _id: id,
        playerId: id,
        status,
        lastSeen: p.lastSeen ?? p.lastSeenAt ?? p.timestamp ?? Date.now(),
        devices: p.devices || [],
        network: p.network || {},
      };

      // Debug: final normalized presence object
      console.debug('[presence normalize] normalized presence:', presenceObj);

      return presenceObj;
    };

    // -----------------------------------------------------------
    // SNAPSHOT
    // -----------------------------------------------------------
    const onSnapshot = (players) => {
      console.log('[socket presence] ← SNAPSHOT raw →', players);
      const snapshot = players.map(normalizePresence).filter(Boolean);

      console.log('[socket presence] ← SNAPSHOT →', snapshot);

      // socketPresenceCallbacks.snapshot.forEach((fn) => fn(snapshot));
      console.warn(
        '[socket presence] getPlayers received → IGNORED for new PresenceEngine',
        players,
      );

      // Do NOT forward to presence subsystem anymore.
      // PresenceContext must ONLY use presence:snapshot + presence:diff.
    };

    // -----------------------------------------------------------
    // presence:heartbeat
    // -----------------------------------------------------------
    const onPresenceHeartbeat = (payload) => {
      const agg = normalizePresence(payload);
      if (!agg) return;

      console.log('[socket presence] ← presence:heartbeat →', agg);

      socketPresenceCallbacks.diff.forEach((fn) =>
        fn({
          joins: [],
          updates: [agg],
          leaves: [],
        }),
      );
    };

    // -----------------------------------------------------------
    // presence:remove   (forced backend removal)
    // -----------------------------------------------------------
    const onPresenceRemove = (payload) => {
      // Legacy: array of sessionIds, no identity. Ignore for new engine.
      if (Array.isArray(payload)) {
        console.warn(
          '[socket presence] presence:remove (sessionIds only) ignored for new PresenceEngine',
          payload,
        );
        return;
      }

      const agg = normalizePresence(payload);
      if (!agg) return;

      console.log(
        '[socket presence] ← presence:remove → LEAVE →',
        agg.playerId,
      );

      socketPresenceCallbacks.diff.forEach((fn) =>
        fn({
          joins: [],
          updates: [],
          leaves: [agg],
        }),
      );
    };

    const onPresenceRemoveDetail = (payload) => {
      const agg = normalizePresence(payload);
      if (!agg) return;
      console.log(
        '[socket presence] ← presence:remove:detail → LEAVE →',
        agg.playerId,
      );
      socketPresenceCallbacks.diff.forEach((fn) =>
        fn({
          joins: [],
          updates: [],
          leaves: [agg],
        }),
      );
    };

    const onConnect = () => {
      if (playerId) socket.emit('addPlayer', playerId);

      // Debug whoami — OK to keep
      socket.emit('debug:whoami', (info) => console.log('[whoami]', info));
    };

    const onWelcome = (m) => console.log('[socket] welcome:', m);

    const isMessageNotification = (n) =>
      n?.type === 'message' || n?.context === 'messages';

    const getCurrentThreadId = () => {
      const path = window.location?.pathname || '';
      const m = path.match(/^\/messenger\/([^/]+)/);
      return m ? m[1] : null;
    };

    const getThreadIdFromNotification = (n) => {
      const collapseKey =
        typeof n?.collapseKey === 'string' ? n.collapseKey : '';
      const m1 = collapseKey.match(/^thread:([^:]+):messages$/);
      if (m1) return m1[1];

      const url = n?.cta?.url || n?.ctaUrl;
      if (typeof url === 'string') {
        const m2 = url.match(/\/messenger\/([^/?#]+)/);
        if (m2) return m2[1];
      }
      return null;
    };

    const isSelfAuthoredMessageNotification = (n) => {
      if (!isMessageNotification(n)) return false;

      const self = window.__jg?.self || {};
      const selfUserId = self.userId ? String(self.userId) : null;
      const selfPlayerId = self.playerId ? String(self.playerId) : null;

      const candidateIds = [
        n?.fromUserId,
        n?.fromPlayerId,
        n?.senderId,
        n?.senderUserId,
        n?.senderPlayerId,
        n?.metadata?.fromUserId,
        n?.metadata?.fromPlayerId,
        n?.metadata?.senderId,
        n?.metadata?.senderUserId,
        n?.metadata?.senderPlayerId,
        n?.metadata?.actorId,
        n?.metadata?.actorUserId,
        n?.metadata?.actorPlayerId,
      ]
        .filter(Boolean)
        .map((v) => String(v));

      if (selfUserId && candidateIds.includes(selfUserId)) return true;
      if (selfPlayerId && candidateIds.includes(selfPlayerId)) return true;

      const title = typeof n?.title === 'string' ? n.title : '';
      if (selfPlayerId && title) {
        const normalized = title
          .replace(/^[^a-z0-9@]+/i, '')
          .replace(/^@/, '');
        if (normalized.toLowerCase().startsWith(selfPlayerId.toLowerCase()))
          return true;
      }

      return false;
    };

    const onNotificationNew = (n) => {
      console.log('[socket] notification:', n);

      // Avoid toasting messages when the user is already viewing that chat thread.
      const currentThreadId = getCurrentThreadId();
      const notifThreadId = getThreadIdFromNotification(n);
      if (
        currentThreadId &&
        notifThreadId &&
        currentThreadId === notifThreadId
      ) {
        return;
      }

      // Avoid toasting your own outbound messages if the backend broadcasts them.
      if (isSelfAuthoredMessageNotification(n)) return;

      showToast(n);
    };

    // 🔊 Prime audio on first interaction
    const prime = () => {
      primeToastAudio();
      window.removeEventListener('click', prime);
      window.removeEventListener('keydown', prime);
      window.removeEventListener('touchstart', prime);
    };

    window.addEventListener('click', prime);
    window.addEventListener('keydown', prime);
    window.addEventListener('touchstart', prime);

    // ⭐ NEW — Presence passthrough handlers
    const onPresenceSnapshot = (snapshot) => {
      if (presenceMetricsDebug) console.log('[presence:snapshot]', snapshot);
      socketPresenceCallbacks.snapshot.forEach((fn) => fn(snapshot));
    };

    const onPresenceDiff = (diff) => {
      if (presenceMetricsDebug) console.log('[presence:diff]', diff);
      socketPresenceCallbacks.diff.forEach((fn) => fn(diff));
    };

    //
    // 🔥 FAANG+ Socket Debugging Overlay
    // Adds logs for ALL presence-related events
    // ------------------------------------------------------
    //

    function debug(event, payload) {
      if (!presenceMetricsDebug) return;
      console.log(
        `%c[socket] ← ${event}`,
        'color:#00bcd4;font-weight:bold;',
        JSON.parse(JSON.stringify(payload)),
      );
    }

    //
    // Register presence-related event listeners
    //

    const handleGetPlayers = (payload) => {
      debug('getPlayers', payload);
      onSnapshot?.(payload);
    };
    socket.on('getPlayers', handleGetPlayers);

    const handlePresenceUpdate = (payload) => {
      console.debug('[socket presence] ← presence:update raw:', payload);

      const agg = normalizePresence(payload);
      if (!agg) {
        console.warn('[socket presence] normalizePresence returned null');
        return;
      }

      // 🔥 Debug the final normalized object reaching the client
      console.debug('[socket presence] presence:update → normalized:', agg);

      // 🚨 OFFLINE = LEAVE
      if (agg.status === 'offline') {
        console.debug(
          `[socket presence] presence:update → OFFLINE → emitting leave for: ${agg.playerId}`,
        );

        socketPresenceCallbacks.diff.forEach((fn) =>
          fn({ joins: [], updates: [], leaves: [agg] }),
        );

        return;
      }

      // ⭐ Normal presence update → emit update diff
      console.debug(
        `[socket presence] presence:update → UPDATE: ${agg.playerId} status=${agg.status}`,
      );

      socketPresenceCallbacks.diff.forEach((fn) =>
        fn({ joins: [], updates: [agg], leaves: [] }),
      );
    };
    socket.on('presence:update', handlePresenceUpdate);

    const handlePresenceHeartbeat = (payload) => {
      debug('presence:heartbeat', payload);
      onPresenceHeartbeat?.(payload);
    };
    socket.on('presence:heartbeat', handlePresenceHeartbeat);

    const handlePresenceRemove = (payload) => {
      debug('presence:remove', payload);
      onPresenceRemove?.(payload);
    };
    socket.on('presence:remove', handlePresenceRemove);

    const handlePresenceRemoveDetail = (payload) => {
      debug('presence:remove:detail', payload);
      onPresenceRemoveDetail?.(payload);
    };
    socket.on('presence:remove:detail', handlePresenceRemoveDetail);

    const handlePresenceSnapshot = (payload) => {
      debug('presence:snapshot', payload);
      onPresenceSnapshot?.(payload);
    };
    socket.on('presence:snapshot', handlePresenceSnapshot);

    const handlePresenceDiff = (payload) => {
      debug('presence:diff', payload);
      onPresenceDiff?.(payload);
    };
    socket.on('presence:diff', handlePresenceDiff);

    if (presenceMetricsDebug) {
      const onPresenceMetrics = (payload) => {
        debug('presence:metrics', payload);
      };
      socket.on('presence:metrics', onPresenceMetrics);
      cleanupPresenceMetrics = () => socket.off('presence:metrics', onPresenceMetrics);
    }

    //
    // Register generic event listeners
    //

    const handleSocketConnect = (payload) => {
      console.log(
        '%c[socket] ← connect',
        'color:#8bc34a;font-weight:bold;',
        payload,
      );
      onConnect?.(payload);
    };
    socket.on('connect', handleSocketConnect);

    const handleSocketWelcome = (payload) => {
      console.log(
        '%c[socket] ← welcome',
        'color:#4caf50;font-weight:bold;',
        payload,
      );
      onWelcome?.(payload);
    };
    socket.on('welcome', handleSocketWelcome);

    const handleSocketNotificationNew = (payload) => {
      console.log(
        '%c[socket] ← notification:new',
        'color:#ff9800;font-weight:bold;',
        payload,
      );
      onNotificationNew?.(payload);
    };
    socket.on('notification:new', handleSocketNotificationNew);

    // If the socket connected before this effect attached listeners, run the connect handler once.
    if (socket.connected) onConnect?.();

    return () => {
      socket.off('connect', handleSocketConnect);
      socket.off('welcome', handleSocketWelcome);
      socket.off('notification:new', handleSocketNotificationNew);
      if (cleanupPresenceMetrics) cleanupPresenceMetrics();

      window.removeEventListener('click', prime);
      window.removeEventListener('keydown', prime);
      window.removeEventListener('touchstart', prime);

      // ⭐ Clean presence listeners
      socket.off('getPlayers', handleGetPlayers);
      socket.off('presence:update', handlePresenceUpdate);
      socket.off('presence:heartbeat', handlePresenceHeartbeat);
      socket.off('presence:remove', handlePresenceRemove);
      socket.off('presence:remove:detail', handlePresenceRemoveDetail);
      socket.off('presence:snapshot', handlePresenceSnapshot);
      socket.off('presence:diff', handlePresenceDiff);
    };
  }, [playerId, socket]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        socketPresence,
        presence: {
          onSnapshot: (fn) => socketPresenceCallbacks.snapshot.add(fn),
          onDiff: (fn) => socketPresenceCallbacks.diff.add(fn),

          offSnapshot: (fn) => socketPresenceCallbacks.snapshot.delete(fn),
          offDiff: (fn) => socketPresenceCallbacks.diff.delete(fn),
        },
        getLatencyMs,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

/* ============================================================
   Debug global exposure
============================================================ */
if (typeof window !== 'undefined') {
  window.__jg = window.__jg || {};
  if (!window.__jg.socket) window.__jg.socket = socket;
  if (!window.__jg.callEvents) window.__jg.callEvents = callEvents;

  console.log('[dbg] window.__jg set with { socket, callEvents }');
}
